import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { THEMES, Theme } from './../constants/themes';
import { AXIS_SCHEDULING_PATH_PREFIX } from '../constants/axis';
interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isSetupComplete: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme Provider Props
interface ThemeProviderProps {
  children: ReactNode;
}

// Theme Provider Component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(THEMES.default);
  const [isSetupComplete, setIsSetupComplete] = useState<boolean>(false);
  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsSetupComplete(true);
      return;
    }

    const themeMapping: Record<string, Theme> = {
      ovum: THEMES.ovum,
      lavenderportal: THEMES.lavender,
      abchospital: THEMES.stluke,
      stluke: THEMES.stluke,
      axis: THEMES.axis,
      default: THEMES.stluke,
    };

    const getClientName = () => {
      const hostname = window.location.hostname?.replace('www.', '') || '';
      return (
        Object.keys(themeMapping).find((key) => hostname.includes(key)) || 'default'
      );
    };

    const determineTheme = () => {
      const pathname = window.location.pathname || '';
      if (pathname.startsWith(AXIS_SCHEDULING_PATH_PREFIX)) {
        return THEMES.axis;
      }
      return themeMapping[getClientName()] || THEMES.abchospital;
    };

    const applyTheme = (markSetupComplete = false) => {
      setTheme(determineTheme());
      if (markSetupComplete) {
        setIsSetupComplete(true);
      }
    };

    const handleLocationChange = () => {
      applyTheme();
    };

    const overrideHistoryMethod = (method: 'pushState' | 'replaceState') => {
      const original = window.history[method];
      (window.history as any)[method] = function (...args: any[]) {
        const result = (original as any).apply(this, args);
        handleLocationChange();
        return result;
      };
      return () => {
        window.history[method] = original;
      };
    };

    applyTheme(true);

    window.addEventListener('popstate', handleLocationChange);
    const restorePushState = overrideHistoryMethod('pushState');
    const restoreReplaceState = overrideHistoryMethod('replaceState');

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      restorePushState();
      restoreReplaceState();
    };
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isSetupComplete }}>
      {children}
    </ThemeContext.Provider>
  );
};
